import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/lib/supabase';
import { apiRequest } from '@/lib/queryClient';
import logger from '@/lib/logger';

// Local database user type
interface DatabaseUser {
  id: number;
  email: string;
  name?: string;
  profilePicture?: string;
  credits: number;
  planTier: string;
  referralCode?: string;
  totalReferrals: number;
  bonusCredits: number;
  planCredits: number;
  consultationCredits: number;
  nextCreditsResetAt?: string;
  nextConsultationResetAt?: string;
  accountType?: 'player' | 'coach';
}

interface AuthContextType {
  user: DatabaseUser | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (accountType?: 'player' | 'coach') => Promise<void>;
  signup: (name: string, email: string, password: string, accountType?: 'player' | 'coach') => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  syncUser: (accountType?: 'player' | 'coach' | null) => Promise<DatabaseUser | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<DatabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [supabase, setSupabase] = useState<any>(null);

  // Refs to prevent race conditions and duplicate processing
  const isProcessingAuth = useRef(false);
  const lastProcessedUserId = useRef<string | null>(null);

  // Self-healing: if supabase is null when an auth action is invoked, try to reinitialize
  const ensureClient = async () => {
    if (supabase) return supabase;
    try {
      const client = await getSupabaseClient();
      setSupabase(client);
      return client;
    } catch (e) {
      throw new Error('Supabase not initialized');
    }
  };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initAuth = async (retryCount = 0): Promise<void> => {
      try {
        const client = await getSupabaseClient();
        setSupabase(client);

        // Get initial session
        const { data: { session } } = await client.auth.getSession();
        setSession(session);

        // Ensure user exists in our database on initial load
        if (session?.user) {
          setIsLoading(true);
          isProcessingAuth.current = true;
          try {
            const dbUser = await ensureUserInDatabase(session.user);
            setUser(dbUser);
            lastProcessedUserId.current = session.user.id;
          } finally {
            setIsLoading(false);
            isProcessingAuth.current = false;
          }
        } else {
          setUser(null);
          setIsLoading(false);
        }

        // Listen for auth changes
        const { data: { subscription } } = client.auth.onAuthStateChange(
          async (event: string, newSession: Session | null) => {
            logger.info('Auth state change:', { event, userId: newSession?.user?.id });

            // Skip if we're already processing or if this is the same user we just processed
            // This prevents duplicate processing from rapid auth events
            if (isProcessingAuth.current) {
              logger.info('Skipping auth state change - already processing');
              return;
            }

            // For INITIAL_SESSION, skip if we already have this user loaded
            if (event === 'INITIAL_SESSION' && newSession?.user?.id === lastProcessedUserId.current) {
              logger.info('Skipping INITIAL_SESSION - user already loaded');
              setSession(newSession);
              return;
            }

            setSession(newSession);

            // Ensure user exists in our database when they sign in
            if (newSession?.user) {
              // Skip if same user already processed (prevents loops)
              // Note: We only use lastProcessedUserId ref here, not the `user` state,
              // because `user` would be a stale closure from when listener was created
              if (newSession.user.id === lastProcessedUserId.current) {
                logger.info('Skipping ensureUserInDatabase - same user already processed');
                return;
              }

              isProcessingAuth.current = true;
              setIsLoading(true);
              try {
                const dbUser = await ensureUserInDatabase(newSession.user);
                setUser(dbUser);
                lastProcessedUserId.current = newSession.user.id;
              } finally {
                setIsLoading(false);
                isProcessingAuth.current = false;
              }
            } else {
              setUser(null);
              lastProcessedUserId.current = null;
              setIsLoading(false);
            }
          }
        );

        unsubscribe = () => subscription.unsubscribe();
      } catch (error) {
        // Retry auth init up to 2 more times to handle cold start delays
        if (retryCount < 2) {
          const delay = 2000 * Math.pow(2, retryCount);
          logger.info(`Auth init failed, retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return initAuth(retryCount + 1);
        }
        logger.error('Failed to initialize auth after retries:', error as any);
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      unsubscribe?.();
    };
  }, []);

  const isAuthenticated = !!session?.user;

  // Ensure user exists in our database
  const ensureUserInDatabase = async (supabaseUser: SupabaseUser, accountTypeOverride?: 'player' | 'coach'): Promise<DatabaseUser | null> => {
    try {
      const userData = {
        id: supabaseUser.id, // Pass the Supabase user ID
        email: supabaseUser.email!,
        name: supabaseUser.user_metadata?.name || supabaseUser.user_metadata?.full_name || '',
        profilePicture: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || '',
        accountType: accountTypeOverride || supabaseUser.user_metadata?.accountType || 'player',
      };

      logger.info('🔄 Ensuring user in database:', { id: userData.id, email: userData.email, accountType: userData.accountType });
      const response = await apiRequest('POST', '/api/users', userData);
      if (!response.ok) {
        logger.error('❌ Failed to ensure user in database - response not OK');
        throw new Error('Failed to sync user to database');
      }

      const dbUser = await response.json() as DatabaseUser;
      logger.info('✅ User ensured in database:', { id: dbUser.id, email: dbUser.email, accountType: dbUser.accountType });
      return dbUser;
    } catch (error) {
      logger.error('Failed to ensure user in database:', error as any);
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    const client = await ensureClient();
    
    const { error } = await client.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const loginWithGoogle = async (accountType: 'player' | 'coach' = 'player') => {
    const client = await ensureClient();

    // Store accountType in localStorage so auth-callback can read it after redirect
    localStorage.setItem('pendingAccountType', accountType);
    logger.info('loginWithGoogle: starting Google OAuth flow', { accountType });

    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const signup = async (name: string, email: string, password: string, accountType: 'player' | 'coach' = 'player') => {
    const client = await ensureClient();

    const { error } = await client.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name,
          accountType: accountType,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    const client = await ensureClient();
    
    const { error } = await client.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
    
    // Clear all user data from localStorage
    localStorage.removeItem('generatedPlan');
    localStorage.removeItem('userFormData');
  };

  const forgotPassword = async (email: string) => {
    const client = await ensureClient();
    
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const refreshUser = async () => {
    if (!user) return;
    
    try {
      const response = await apiRequest('GET', `/api/users/${user.id}`);
      if (response.ok) {
        const updatedUser = await response.json() as DatabaseUser;
        setUser(updatedUser);
      }
    } catch (error) {
      logger.error('Failed to refresh user:', error as any);
    }
  };

  // Sync user: ensures user exists in database from current session
  // This is called from auth-callback to handle email confirmation flow
  // Memoized to prevent useEffect re-runs in auth-callback
  const syncUser = useCallback(async (accountTypeParam?: 'player' | 'coach' | null): Promise<DatabaseUser | null> => {
    // Prevent duplicate sync calls
    if (isProcessingAuth.current) {
      logger.info('syncUser: skipping - already processing');
      return user;
    }

    try {
      isProcessingAuth.current = true;
      const client = await ensureClient();
      const { data: { session: currentSession } } = await client.auth.getSession();

      if (!currentSession?.user) {
        logger.warn('syncUser called but no session available');
        return null;
      }

      // Skip if same user already processed
      if (currentSession.user.id === lastProcessedUserId.current && user) {
        logger.info('syncUser: user already synced, returning cached user');
        setSession(currentSession);
        return user;
      }

      // Priority: 1) Parameter (from OAuth state), 2) localStorage (backward compat), 3) None
      let accountTypeOverride: 'player' | 'coach' | undefined = undefined;

      if (accountTypeParam) {
        accountTypeOverride = accountTypeParam;
        logger.info('syncUser: using account type from OAuth state parameter', { accountType: accountTypeParam });
      } else {
        const pendingAccountType = localStorage.getItem('pendingAccountType') as 'player' | 'coach' | null;
        if (pendingAccountType) {
          accountTypeOverride = pendingAccountType;
          logger.info('syncUser: using pending account type from localStorage', { pendingAccountType });
        }
      }

      // Clean up localStorage regardless
      localStorage.removeItem('pendingAccountType');

      setSession(currentSession);
      const dbUser = await ensureUserInDatabase(currentSession.user, accountTypeOverride);
      setUser(dbUser);
      lastProcessedUserId.current = currentSession.user.id;
      return dbUser;
    } catch (error) {
      logger.error('Failed to sync user:', error as any);
      return null;
    } finally {
      isProcessingAuth.current = false;
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated,
    login,
    loginWithGoogle,
    signup,
    logout,
    forgotPassword,
    refreshUser,
    syncUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}